const extractId = (v) => { if (!v) return ''; if (typeof v === 'string' || typeof v === 'number') return String(v).trim(); return String(v._id || v.id || v); };
const compareIds = (a, b) => extractId(a) === extractId(b);

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Avatar,
  Checkbox,
  FormGroup,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  AdminPanelSettings,
  School,
  Person,
  MenuBook,
  Visibility,
  VisibilityOff,
  Security,
  Lock,
  UploadFile,
  Download,
  Description,
  Payment,
  AccountBalance
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { exportToExcel } from '../../services/exportService';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [roles, setRoles] = useState([]); // ⭐ État pour les rôles
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // État pour le modal utilisateur
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    prenom: '',
    role: 'CONSULTANT',
    etudiant_id: '',
    enseignant_id: '',
    enabled: true
  });

  // États pour les filtres cascade
  const [annees, setAnnees] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [selectedFiliere, setSelectedFiliere] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');

  // État pour l'import JSON
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ⭐ État pour le modal des permissions
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsSearchTerm, setPermissionsSearchTerm] = useState('');

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  // ============ CHARGEMENT DES DONNÉES ============

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 [Users] Chargement des données...');
      
      const headers = getAuthHeaders();
      
      const [usersRes, etudiantsRes, enseignantsRes, rolesRes, anneesRes, filieresRes, niveauxRes, inscriptionsRes] = await Promise.all([
        axios.get('/api/users', { headers }),
        axios.get('/api/etudiants?limit=100000', { headers }),
        axios.get('/api/enseignants', { headers }),
        axios.get('/api/roles', { headers }),
        axios.get('/api/annees', { headers }),
        axios.get('/api/filieres', { headers }),
        axios.get('/api/niveaux', { headers }),
        axios.get('/api/inscriptions', { headers })
      ]);
      
      const usersData = usersRes.data || [];
      const etudiantsData = etudiantsRes.data?.data || etudiantsRes.data || [];
      const ensData = enseignantsRes.data || [];
      const rolesData = rolesRes.data || [];
      
      console.log('📊 Utilisateurs chargés:', usersData.length);
      console.log('📊 Étudiants chargés:', etudiantsData.length);
      console.log('📊 Enseignants chargés:', ensData.length);
      console.log('📊 Rôles chargés:', rolesData.length);
      
      setUsers(usersData);
      setEtudiants(etudiantsData);
      setEnseignants(ensData);
      setRoles(rolesData);
      setAnnees(anneesRes.data || []);
      setFilieres(filieresRes.data || []);
      setNiveaux(niveauxRes.data || []);
      setInscriptions(inscriptionsRes.data?.data || inscriptionsRes.data || []);
      setError('');
    } catch (err) {
      console.error('❌ Erreur chargement:', err);
      console.error('❌ Réponse erreur:', err.response?.data);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  // ============ CHARGEMENT DES PERMISSIONS ============

  const loadPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const headers = getAuthHeaders();
      
      // Charger toutes les permissions disponibles
      const permsRes = await axios.get('/api/permissions', { headers });
      setAllPermissions(permsRes.data || []);
      
      // Charger les permissions de l'utilisateur
      if (permissionsUser) {
        const userRes = await axios.get(`/api/users/${permissionsUser._id}`, { headers });
        setUserPermissions(userRes.data.permissionsExplicites || []);
      }
      
      setError('');
    } catch (err) {
      console.error('❌ Erreur chargement permissions:', err);
      setError('Impossible de charger les permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  // ============ FONCTIONS UTILITAIRES ============

  const getRoleIcon = (role) => {
    switch(role) {
      case 'ADMIN': return <AdminPanelSettings fontSize="small" />;
      case 'DIRECTEUR': return <AdminPanelSettings fontSize="small" />;
      case 'SCOLARITE': return <MenuBook fontSize="small" />;
      case 'CHEF_FILIERE': return <AdminPanelSettings fontSize="small" />;
      case 'DIRECTION_FINANCIERE': return <Payment fontSize="small" />;
      case 'DIRECTION_GENERALE': return <AccountBalance fontSize="small" />;
      case 'COORDINATEUR_PEDAGOGIQUE': return <School fontSize="small" />;
      case 'ENSEIGNANT': return <School fontSize="small" />;
      case 'ETUDIANT': return <Person fontSize="small" />;
      default: return <Person fontSize="small" />;
    }
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'ADMIN': return { bg: '#fef3c7', color: '#92400e' };
      case 'DIRECTEUR': return { bg: '#ede9fe', color: '#6d28d9' };
      case 'SCOLARITE': return { bg: '#cffafe', color: '#0891b2' };
      case 'CHEF_FILIERE': return { bg: '#ccfbf1', color: '#0f766e' };
      case 'DIRECTION_FINANCIERE': return { bg: '#fef3c7', color: '#b45309' };
      case 'DIRECTION_GENERALE': return { bg: '#e0e7ff', color: '#4338ca' };
      case 'COORDINATEUR_PEDAGOGIQUE': return { bg: '#ccfbf1', color: '#0d9488' };
      case 'ENSEIGNANT': return { bg: '#dbeafe', color: '#1e40af' };
      case 'ETUDIANT': return { bg: '#dcfce7', color: '#166534' };
      default: return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const getLinkedEntity = (user) => {
    if (user.role === 'ETUDIANT' && user.etudiant_id) {
      const etudiant = etudiants.find(e => 
        String(e.etudiant_id) === String(user.etudiant_id) ||
        String(e._id) === String(user.etudiant_id)
      );
      return etudiant ? `${etudiant.Nom || etudiant.nom || etudiant.etudiant_nom || ''} ${etudiant.Prénom || etudiant.prenom || etudiant.etudiant_prenom || ''}` : user.etudiant_id;
    }
    if (user.role === 'ENSEIGNANT' && user.enseignant_id) {
      const enseignant = enseignants.find(e => 
        String(e.matricule) === String(user.enseignant_id) ||
        String(e._id) === String(user.enseignant_id)
      );
      return enseignant ? `${enseignant.nom} ${enseignant.prenom}` : user.enseignant_id;
    }
    return '-';
  };

  const filteredData = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return (user.nom || '').toLowerCase().includes(search) ||
           (user.prenom || '').toLowerCase().includes(search) ||
           (user.email || '').toLowerCase().includes(search) ||
           (user.role || '').toLowerCase().includes(search);
  });

  // Filtrer les étudiants selon les filtres cascade (année → filière → niveau)
  const filteredEtudiants = useMemo(() => {
    let idsInscrits = null;
    if (selectedAnnee || selectedFiliere || selectedNiveau) {
      let inscrits = [...inscriptions].filter(i => i.isActive !== false && i.statut !== 'ANNULEE');
      if (selectedAnnee) {
        inscrits = inscrits.filter(i => {
          const insAnnee = i.anneeUniversitaire?._id || i.annee_universitaire_id;
          return compareIds(insAnnee, selectedAnnee);
        });
      }
      if (selectedFiliere) {
        inscrits = inscrits.filter(i =>
          compareIds(i.filiere?._id || i.filiere_id, selectedFiliere)
        );
      }
      if (selectedNiveau) {
        inscrits = inscrits.filter(i =>
          compareIds(i.niveau?._id || i.niveau_id, selectedNiveau)
        );
      }
      idsInscrits = new Set(inscrits.map(i => extractId(i.etudiant?._id || i.etudiant_id)));
    }
    if (idsInscrits) {
      return etudiants.filter(e => idsInscrits.has((e._id || e.id).toString()));
    }
    return etudiants;
  }, [etudiants, inscriptions, selectedAnnee, selectedFiliere, selectedNiveau]);

  const filieresFiltrees = useMemo(() => {
    if (!selectedAnnee) return filieres;
    const annee = annees.find(a => compareIds(a._id, selectedAnnee));
    if (!annee) return filieres;
    return filieres.filter(f =>
      compareIds(f.annee_id, annee._id) || compareIds(f.annee_id, annee.annee_id)
    );
  }, [filieres, annees, selectedAnnee]);

  const niveauxFiltres = useMemo(() => {
    if (!selectedFiliere) return niveaux;
    const filiere = filieres.find(f => compareIds(f._id, selectedFiliere));
    if (!filiere?.niveaux) return niveaux;
    const niveauIds = new Set(filiere.niveaux.map(fn => extractId(fn.niveau_id)));
    return niveaux.filter(n => niveauIds.has(extractId(n.niveau_id || n._id || n.id)));
  }, [niveaux, filieres, selectedFiliere]);

  // Reset cascade quand l'année change
  useEffect(() => {
    setSelectedFiliere('');
    setSelectedNiveau('');
  }, [selectedAnnee]);

  useEffect(() => {
    setSelectedNiveau('');
  }, [selectedFiliere]);

  // ============ GESTION DES MODALS ============

  // Modal utilisateur
  const handleOpenModal = (mode, user = null) => {
    console.log('🔄 Ouvrir modal - Mode:', mode, 'User:', user);
    setModalMode(mode);
    if (user) {
      setSelectedUser(user);
      const linkedEnseignant = user.role === 'ENSEIGNANT' && user.enseignant_id
        ? enseignants.find(e =>
            String(e.matricule) === String(user.enseignant_id) ||
            String(e._id) === String(user.enseignant_id)
          )
        : null;
      setFormData({
        email: user.email || '',
        password: '',
        nom: user.nom || '',
        prenom: user.prenom || '',
        role: user.role || 'CONSULTANT',
        etudiant_id: user.etudiant_id || '',
        enseignant_id: linkedEnseignant ? (linkedEnseignant.matricule || linkedEnseignant._id) : (user.enseignant_id || ''),
        enabled: user.enabled !== undefined ? user.enabled : true
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      nom: '',
      prenom: '',
      role: 'CONSULTANT',
      etudiant_id: '',
      enseignant_id: '',
      enabled: true
    });
    setShowPassword(false);
  };

  // ============ IMPORT / EXPORT (JSON / EXCEL) ============

  const COLONNES_UTILISATEURS = [
    { key: 'nom', label: 'Nom' },
    { key: 'prenom', label: 'Prénom' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Rôle' }
  ];

  const handleExportExcel = () => {
    if (users.length === 0) {
      setError('Aucun utilisateur à exporter');
      return;
    }
    const data = users.map(u => ({
      nom: u.nom || '',
      prenom: u.prenom || '',
      email: u.email || '',
      role: u.role || ''
    }));
    exportToExcel(data, 'Liste_Utilisateurs', COLONNES_UTILISATEURS);
  };

  const telechargerModele = () => {
    const modele = [{
      'Nom': 'Exemple',
      'Prénom': 'Sara',
      'Email': 'sara.exemple@sup2i.ac',
      'Rôle': 'ETUDIANT',
      'Mot de passe': ''
    }];
    const ws = XLSX.utils.json_to_sheet(modele);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Utilisateurs');
    XLSX.writeFile(wb, 'modele_import_utilisateurs.xlsx');
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportData([]);
    setImportResult(null);
    setError('');
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportData([]);
    setImportResult(null);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    const nomFichier = file.name.toLowerCase();
    try {
      let data;
      if (nomFichier.endsWith('.json')) {
        const texte = await file.text();
        data = JSON.parse(texte);
      } else {
        const buffer = await file.arrayBuffer();
        const classeur = XLSX.read(buffer, { type: 'array' });
        const feuille = classeur.Sheets[classeur.SheetNames[0]];
        const lignes = XLSX.utils.sheet_to_json(feuille, { defval: '' });
        const normaliserCle = (s) => String(s)
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .toLowerCase().replace(/[^a-z0-9]/g, '');
        const lire = (l, ...cles) => {
          for (const cle of cles) {
            const cible = normaliserCle(cle);
            for (const k of Object.keys(l)) {
              if (normaliserCle(k) === cible) {
                const v = l[k];
                if (v !== undefined && v !== null && String(v).trim() !== '') {
                  return String(v).trim();
                }
              }
            }
          }
          return '';
        };
        data = lignes.map(l => ({
          nom: lire(l, 'Nom', 'nom', 'NOM'),
          prenom: lire(l, 'Prénom', 'Prenom', 'prenom', 'PRENOM'),
          email: lire(l, 'Email', 'email', 'EMAIL'),
          role: lire(l, 'Rôle', 'Role', 'role').toUpperCase(),
          password: lire(l, 'Mot de passe', 'MotDePasse', 'password')
        })).filter(x => x.nom || x.prenom || x.email);
      }

      if (Array.isArray(data)) {
        setImportData(data);
        setError('');
      } else {
        setError('Le fichier doit contenir un tableau d\'utilisateurs');
        setImportData([]);
      }
    } catch {
      setError('Fichier invalide (JSON ou Excel)');
      setImportData([]);
    }
  };

  const handleImport = async () => {
    if (importData.length === 0) return;
    setImporting(true);
    setError('');
    try {
      const res = await axios.post(`/api/users/import-users`, { users: importData }, {
        headers: getAuthHeaders()
      });
      setImportResult(res.data);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  };

  // ⭐ Modal permissions
  const handleOpenPermissions = async (user) => {
    setPermissionsUser(user);
    setPermissionsModalOpen(true);
    await loadPermissions();
  };

  const handleClosePermissions = () => {
    setPermissionsModalOpen(false);
    setPermissionsUser(null);
    setUserPermissions([]);
    setPermissionsSearchTerm('');
  };

  // ============ CRUD UTILISATEURS ============

  const handleSubmit = async () => {
    if (!formData.email || !formData.nom || !formData.prenom) {
      setError('Veuillez remplir tous les champs obligatoires (Email, Nom, Prénom)');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Veuillez saisir un email valide');
      return;
    }

    if (modalMode === 'add' && !formData.password) {
      setError('Le mot de passe est obligatoire pour un nouvel utilisateur');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (formData.role === 'ETUDIANT' && !formData.etudiant_id) {
      setError('Veuillez sélectionner un étudiant associé');
      return;
    }

    if (formData.role === 'ENSEIGNANT' && !formData.enseignant_id) {
      setError('Veuillez sélectionner un enseignant associé');
      return;
    }

    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const userData = {
        email: formData.email,
        nom: formData.nom,
        prenom: formData.prenom,
        role: formData.role,
        enabled: formData.enabled
      };

      if (formData.role === 'ETUDIANT') {
        userData.etudiant_id = formData.etudiant_id || null;
      }

      if (formData.role === 'ENSEIGNANT') {
        userData.enseignant_id = formData.enseignant_id || null;
      }

      if (formData.password && formData.password.length >= 6) {
        userData.password = formData.password;
      }
      
      console.log('📤 Mode:', modalMode);
      console.log('📤 Données envoyées:', userData);
      
      let response;
      if (modalMode === 'add') {
        if (!userData.password) {
          setError('Le mot de passe est obligatoire');
          setLoading(false);
          return;
        }
        response = await axios.post('/api/users', userData, { headers });
      } else {
        console.log(`🔗 PUT /api/users/${selectedUser._id}`);
        response = await axios.put(
          `/api/users/${selectedUser._id}`,
          userData,
          { headers }
        );
      }
      
      console.log('✅ Réponse:', response.data);
      
      if (response.data.success || response.status === 201 || response.status === 200) {
        setSuccessMessage(response.data.message || 'Opération réussie');
        await loadData();
        handleCloseModal();
      } else {
        setError(response.data.message || 'Erreur lors de l\'opération');
      }
    } catch (err) {
      console.error('❌ Erreur détaillée:', err);
      console.error('❌ Réponse erreur:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 
                           err.response?.data?.message || 
                           err.message ||
                           'Erreur lors de l\'opération';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Supprimer définitivement ${user.nom} ${user.prenom} ?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/users/${user._id}`, { headers });
      setSuccessMessage('Utilisateur supprimé avec succès');
      await loadData();
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // ============ GESTION DES PERMISSIONS ============

  const handlePermissionToggle = (permId) => {
    setUserPermissions(prev => 
      prev.includes(permId) 
        ? prev.filter(id => id !== permId)
        : [...prev, permId]
    );
  };

  const handleSavePermissions = async () => {
    try {
      setPermissionsLoading(true);
      const headers = getAuthHeaders();
      
      await axios.put(
        `/api/users/${permissionsUser._id}/permissions`,
        { permissions: userPermissions },
        { headers }
      );
      
      setSuccessMessage('Permissions mises à jour avec succès');
      await loadData();
      setTimeout(() => setSuccessMessage(''), 3000);
      handleClosePermissions();
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const getPermissionsByCategory = () => {
    const filtered = allPermissions.filter(p => 
      p.nom?.toLowerCase().includes(permissionsSearchTerm.toLowerCase()) ||
      p.code?.toLowerCase().includes(permissionsSearchTerm.toLowerCase())
    );
    
    const grouped = {};
    filtered.forEach(perm => {
      const category = perm.categorie || 'AUTRES';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(perm);
    });
    return grouped;
  };

  // ============ RENDU ============

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            👥 Gestion des Utilisateurs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} utilisateur(s) trouvé(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExportExcel}>
            Exporter Excel
          </Button>
          <Button variant="outlined" startIcon={<UploadFile />} onClick={handleOpenImportDialog}>
            Importer Excel
          </Button>
          <Button variant="outlined" startIcon={<Description />} onClick={telechargerModele}>
            Modèle Excel
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouvel Utilisateur
          </Button>
        </Stack>
      </Box>

      {/* Recherche */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, prénom, email ou rôle..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Utilisateur</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Rôle</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Lié à</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => {
              const roleStyle = getRoleColor(user.role);
              return (
                <TableRow key={user._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: roleStyle.color }}>
                        {(user.prenom || 'U')[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.nom} {user.prenom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user._id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getRoleIcon(user.role)}
                      label={user.role}
                      size="small"
                      sx={{ bgcolor: roleStyle.bg, color: roleStyle.color }}
                    />
                  </TableCell>
                  <TableCell>{getLinkedEntity(user)}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.enabled ? '✅ Actif' : '❌ Inactif'}
                      size="small"
                      sx={{ bgcolor: user.enabled ? '#dcfce7' : '#fee2e2', color: user.enabled ? '#166534' : '#991b1b' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', user)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Gérer les permissions">
                        <IconButton 
                          size="small" 
                          color="info" 
                          onClick={() => handleOpenPermissions(user)}
                        >
                          <Security fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(user)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucun utilisateur trouvé</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      {/* ============================================ */}
      {/* MODAL UTILISATEUR (AJOUT / MODIFICATION) */}
      {/* ============================================ */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter un utilisateur' : '✏️ Modifier l\'utilisateur'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={modalMode === 'add' ? 'Mot de passe *' : 'Nouveau mot de passe'}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  margin="normal"
                  required={modalMode === 'add'}
                  placeholder={modalMode === 'edit' ? 'Laisser vide pour garder le mot de passe actuel' : ''}
                  helperText={modalMode === 'edit' ? 'Laisser vide pour conserver le mot de passe actuel' : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Rôle *</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setFormData({
                        ...formData,
                        role: newRole,
                        etudiant_id: newRole === 'ETUDIANT' ? formData.etudiant_id : '',
                        enseignant_id: newRole === 'ENSEIGNANT' ? formData.enseignant_id : ''
                      });
                    }}
                    label="Rôle *"
                  >
                    {/* ⭐ Rôles par défaut */}
                    <MenuItem value="ADMIN">👑 Administrateur</MenuItem>
                    <MenuItem value="DIRECTEUR">🎯 Directeur</MenuItem>
                    <MenuItem value="SCOLARITE">📚 Scolarité</MenuItem>
                    <MenuItem value="CHEF_FILIERE">🧭 Chef de filière</MenuItem>
                    <MenuItem value="DIRECTION_FINANCIERE">💳 Direction financière</MenuItem>
                    <MenuItem value="DIRECTION_GENERALE">🏛️ Direction générale (lecture seule)</MenuItem>
                    <MenuItem value="COORDINATEUR_PEDAGOGIQUE">🎓 Coordinateur pédagogique (lecture seule)</MenuItem>
                    <MenuItem value="ENSEIGNANT">👨‍🏫 Enseignant</MenuItem>
                    <MenuItem value="ETUDIANT">👨‍🎓 Étudiant</MenuItem>
                    <MenuItem value="CONSULTANT">👁️ Consultant</MenuItem>
                    
                    {/* ⭐ Rôles dynamiques depuis la base de données */}
                    {roles.map((role) => (
                      <MenuItem key={role._id} value={role.code}>
                        {role.icon || '📋'} {role.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    />
                  }
                  label={formData.enabled ? '✅ Compte activé' : '❌ Compte désactivé'}
                  sx={{ mt: 3 }}
                />
              </Grid>
              
              {/* Étudiant associé */}
              {formData.role === 'ETUDIANT' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 600, color: '#374151' }}>
                      Filtrer par inscription
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Année</InputLabel>
                      <Select value={selectedAnnee} onChange={(e) => setSelectedAnnee(e.target.value)} label="Année">
                        <MenuItem value="">Toutes les années</MenuItem>
                        {annees.map(a => (
                          <MenuItem key={a._id} value={a._id?.toString() || a.id?.toString()}>
                            {a.intitule || a.libelle || a.annee}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filière</InputLabel>
                      <Select value={selectedFiliere} onChange={(e) => setSelectedFiliere(e.target.value)} label="Filière">
                        <MenuItem value="">Toutes les filières</MenuItem>
                        {filieresFiltrees.map(f => (
                          <MenuItem key={f._id} value={f._id?.toString() || f.id?.toString()}>
                            {f.libelle || f.intitule || f.nom}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Niveau</InputLabel>
                      <Select value={selectedNiveau} onChange={(e) => setSelectedNiveau(e.target.value)} label="Niveau">
                        <MenuItem value="">Tous les niveaux</MenuItem>
                        {niveauxFiltres.map(n => (
                          <MenuItem key={n._id} value={n._id?.toString() || n.id?.toString()}>
                            {n.intitule || n.libelle || n.nom}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel>Étudiant associé *</InputLabel>
                      <Select
                        value={formData.etudiant_id}
                        onChange={(e) => setFormData({ ...formData, etudiant_id: e.target.value })}
                        label="Étudiant associé *"
                      >
                        <MenuItem value="">Sélectionner un étudiant</MenuItem>
                        {filteredEtudiants.map((e) => {
                          const id = e._id || e.etudiant_id;
                          const nom = e.Nom || e.nom || e.etudiant_nom || 'Inconnu';
                          const prenom = e.Prénom || e.prenom || e.etudiant_prenom || 'Inconnu';
                          const cne = e.CNE || e.cne || e.etudiant_cne_masar || 'N/A';
                          return (
                            <MenuItem key={id} value={id}>
                              {nom} {prenom} ({cne})
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {filteredEtudiants?.length || 0} étudiant(s) trouvé(s)
                      {(selectedAnnee || selectedFiliere || selectedNiveau) ? ' (filtrés)' : ' (tous)'}
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Enseignant associé */}
              {formData.role === 'ENSEIGNANT' && (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Enseignant associé *</InputLabel>
                    <Select
                      value={formData.enseignant_id}
                      onChange={(e) => setFormData({ ...formData, enseignant_id: e.target.value })}
                      label="Enseignant associé *"
                    >
                      <MenuItem value="">Sélectionner un enseignant</MenuItem>
                      {enseignants.map((e) => {
                        const id = e.matricule || e._id;
                        const nom = e.nom || 'Inconnu';
                        const prenom = e.prenom || 'Inconnu';
                        return (
                          <MenuItem key={id} value={id}>
                            {nom} {prenom} ({e.matricule || e.nSomme || 'N/A'})
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<Save />} 
            sx={{ bgcolor: '#667eea' }}
            disabled={
              !formData.email || 
              !formData.nom || 
              !formData.prenom || 
              (modalMode === 'add' && !formData.password) ||
              (formData.role === 'ETUDIANT' && !formData.etudiant_id) ||
              (formData.role === 'ENSEIGNANT' && !formData.enseignant_id)
            }
          >
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* ⭐ MODAL PERMISSIONS */}
      {/* ============================================ */}
      <Dialog 
        open={permissionsModalOpen} 
        onClose={handleClosePermissions} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">🔑 Gestion des permissions</Typography>
              <Typography variant="body2" color="text.secondary">
                {permissionsUser?.nom} {permissionsUser?.prenom} ({permissionsUser?.email})
              </Typography>
              <Chip 
                label={permissionsUser?.role} 
                size="small" 
                color="primary" 
                sx={{ mt: 1 }} 
              />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<Refresh />}
                onClick={loadPermissions}
              >
                Actualiser
              </Button>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher une permission..."
            value={permissionsSearchTerm}
            onChange={(e) => setPermissionsSearchTerm(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          {permissionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
              {allPermissions.length === 0 ? (
                <Alert severity="info">
                  Aucune permission disponible. Initialisez les permissions avec le script.
                </Alert>
              ) : (
                Object.entries(getPermissionsByCategory()).map(([category, perms]) => (
                  <Card key={category} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        {category}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={1}>
                        {perms.map((perm) => (
                          <Grid item xs={12} sm={6} md={4} key={perm._id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={userPermissions.includes(perm._id)}
                                  onChange={() => handlePermissionToggle(perm._id)}
                                  color="primary"
                                  size="small"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{perm.nom}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {perm.code}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePermissions} startIcon={<Cancel />}>
            Annuler
          </Button>
          <Button 
            onClick={handleSavePermissions} 
            variant="contained" 
            startIcon={<Save />}
            sx={{ bgcolor: '#667eea' }}
            disabled={permissionsLoading}
          >
            {permissionsLoading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Import JSON */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>Importer des utilisateurs (Excel / JSON)</DialogTitle>
        <DialogContent>
          {!importResult ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionnez un fichier Excel (.xlsx) ou JSON contenant les colonnes :
                {' '}<code>Nom</code>, <code>Prénom</code>, <code>Email</code>, <code>Rôle</code>, <code>Mot de passe</code> (optionnel).
                Les comptes absents sont créés ; si l'email existe déjà, seul le mot de passe est réinitialisé.
              </Typography>
              <input
                type="file"
                accept=".xlsx,.xls,.json"
                onChange={handleFileChange}
                style={{ marginBottom: 16 }}
              />
              {importFile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Fichier chargé : {importFile.name} — {importData.length} utilisateur(s) trouvé(s)
                </Alert>
              )}
              {importData.length > 0 && (
                <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
                  {importData.slice(0, 20).map((u, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {i + 1}. {u.nom} {u.prenom} — {u.email}
                    </Typography>
                  ))}
                  {importData.length > 20 && (
                    <Typography variant="caption" color="text.secondary">
                      ... et {importData.length - 20} autre(s)
                    </Typography>
                  )}
                </Paper>
              )}
            </>
          ) : (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip label={`${importResult.created?.length || 0} créé(s)`} color="success" />
                <Chip label={`${importResult.updated?.length || 0} réinitialisé(s)`} color="info" />
                <Chip label={`${importResult.errors?.length || 0} erreur(s)`} color="error" />
              </Stack>
              {importResult.updated?.length > 0 && (
                <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1, mb: 2, bgcolor: '#eff6ff' }}>
                  {importResult.updated.map((u, i) => (
                    <Typography key={i} variant="caption" display="block">
                      🔑 {u.nom} {u.prenom} — {u.email} (mot de passe réinitialisé)
                    </Typography>
                  ))}
                </Paper>
              )}
              {importResult.created?.length > 0 && (
                <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1, mb: 2 }}>
                  {importResult.created.map((u, i) => (
                    <Typography key={i} variant="caption" display="block">
                      ✅ {u.nom} {u.prenom} — {u.email}
                    </Typography>
                  ))}
                </Paper>
              )}
              {importResult.errors?.length > 0 && (
                <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1, bgcolor: '#fef2f2' }}>
                  {importResult.errors.map((e, i) => (
                    <Typography key={i} variant="caption" display="block" color="error">
                      ❌ Ligne {e.ligne} — {e.erreur}
                    </Typography>
                  ))}
                </Paper>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>
            {importResult ? 'Fermer' : 'Annuler'}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importData.length === 0 || importing}
              startIcon={importing ? <CircularProgress size={20} /> : <UploadFile />}
            >
              {importing ? 'Importation...' : `Importer ${importData.length} utilisateur(s)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;


